import { lazy, Suspense, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
  useParams,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import { ChatProvider } from './contexts/ChatContext';
import { Toaster } from 'react-hot-toast';
import { TooltipProvider } from './components/ui/Tooltip';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import PageTransition from './components/effects/PageTransition';
import MfaChallenge from './components/auth/MfaChallenge';
import { Home } from 'lucide-react';

const Login = lazy(() => import('./pages/Login'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DataInhaal = lazy(() => import('./pages/DataInhaal'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const RegieProject = lazy(() => import('./pages/RegieProject'));
const HandboekRegie = lazy(() => import('./pages/HandboekRegie'));
const ContractSigning = lazy(() => import('./pages/ContractSigning'));
const MeerwerkSigning = lazy(() => import('./pages/MeerwerkSigning'));
const ClientNotifications = lazy(() => import('./pages/ClientNotifications'));
const ClusterSigning = lazy(() => import('./pages/ClusterSigning'));
const OfferteSigning = lazy(() => import('./pages/OfferteSigning'));
const Meldingen = lazy(() => import('./pages/Meldingen'));
const AppointmentResponse = lazy(() => import('./pages/AppointmentResponse'));
const LeadDetail = lazy(() => import('./pages/LeadDetail'));
const AiMeetstaatTest = lazy(() => import('./pages/AiMeetstaatTest'));
const WhatsAppInbox = lazy(() => import('./pages/WhatsAppInbox'));
const Belregister = lazy(() => import('./pages/Belregister'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const PaymentThankYou = lazy(() => import('./pages/PaymentThankYou'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const DesignSystem = lazy(() => import('./pages/DesignSystem'));
const AiOrganisatie = lazy(() => import('./pages/AiOrganisatie'));
const AiOrganisatieGoedkeuringen = lazy(() => import('./pages/AiOrganisatieGoedkeuringen'));
const AiOrganisatieAgent = lazy(() => import('./pages/AiOrganisatieAgent'));
const Chief = lazy(() => import('./pages/Chief'));
const MissionControlPage = lazy(() => import('./pages/MissionControl'));
const PreviewPLDashboard = lazy(() => import('./pages/PreviewPLDashboard'));
const PreviewFacturen = lazy(() => import('./pages/PreviewFacturen'));
const PreviewProjectDetail = lazy(() => import('./pages/PreviewProjectDetail'));
const PreviewAanmaning = lazy(() => import('./pages/PreviewAanmaning'));

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-foreground-secondary">Laden...</div>
    </div>
  );
}

/**
 * Oude/losse adressen uit meldingen, pushberichten en edge functions
 * (/settings, /inbox, /clusters, …) bestaan niet als route; ze horen bij een
 * tab van het dashboard. Querystring (bv. ?section=) gaat mee.
 */
function NaarDashboardTab({ tab }: { tab: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', tab);
  return <Navigate to={`/?${params.toString()}`} replace />;
}

/** Oude leadlinks (/leads/:id) → /lead/:id. */
function NaarLead() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/lead/${id}`} replace />;
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <div className="text-center space-y-4">
        <h1 className="text-6xl font-bold text-foreground">404</h1>
        <p className="text-lg text-foreground-secondary">Pagina niet gevonden</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-xl font-medium hover:bg-brand-active transition-colors"
        >
          <Home className="w-4 h-4" />
          Terug naar dashboard
        </Link>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading, mfaRequired } = useAuth();
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!loading) {
      setShowError(false);
      return;
    }
    const id = setTimeout(() => setShowError(true), 15000);
    return () => clearTimeout(id);
  }, [loading]);

  if (loading && !showError) {
    return <LoadingFallback />;
  }

  if (loading && showError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-center space-y-4 max-w-sm mx-auto px-4">
          <p className="text-foreground-secondary">
            Er is een probleem met het laden van je sessie.
          </p>
          <button
            onClick={() => {
              localStorage.clear();
              window.location.href = '/login';
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-xl font-medium hover:bg-brand-active transition-colors"
          >
            Opnieuw inloggen
          </button>
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    return <Navigate to="/login" replace />;
  }

  // MFA-login-uitdaging: ingelogd (aal1) maar een geverifieerde factor aanwezig -> eerst de code.
  if (mfaRequired) {
    return <MfaChallenge />;
  }

  return (
    <Layout>
      <PageTransition>{children}</PageTransition>
    </Layout>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200}>
        <RealtimeProvider>
          <BrowserRouter>
            <ThemeProvider>
              <AuthProvider>
                <ChatProvider>
                  <ErrorBoundary fallbackMessage="Er is een fout opgetreden bij het laden van de pagina">
                    <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/invite/:token" element={<AcceptInvite />} />
                        <Route path="/sign/:token" element={<ContractSigning />} />
                        <Route path="/meerwerk/:token" element={<MeerwerkSigning />} />
                        <Route path="/cluster-sign/:token" element={<ClusterSigning />} />
                        <Route path="/offerte/tekenen/:token" element={<OfferteSigning />} />
                        <Route path="/afspraak/:token" element={<AppointmentResponse />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/betaling/bedankt" element={<PaymentThankYou />} />
                        <Route path="/design-system" element={<DesignSystem />} />
                        <Route
                          path="/design-preview/pl-dashboard"
                          element={<PreviewPLDashboard />}
                        />
                        <Route path="/design-preview/facturen" element={<PreviewFacturen />} />
                        <Route
                          path="/design-preview/projectdetail"
                          element={<PreviewProjectDetail />}
                        />
                        <Route path="/design-preview/aanmaning" element={<PreviewAanmaning />} />
                        <Route
                          path="/"
                          element={
                            <ProtectedRoute>
                              <Dashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute>
                              <Dashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/lead/:id"
                          element={
                            <ProtectedRoute>
                              <LeadDetail />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/project/:id"
                          element={
                            <ProtectedRoute>
                              <ProjectDetail />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/regie/:itemId"
                          element={
                            <ProtectedRoute>
                              <RegieProject />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/handboek/regie"
                          element={
                            <ProtectedRoute>
                              <HandboekRegie />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/notifications"
                          element={
                            <ProtectedRoute>
                              <ClientNotifications />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/meldingen"
                          element={
                            <ProtectedRoute>
                              <Meldingen />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/whatsapp"
                          element={
                            <ProtectedRoute>
                              <WhatsAppInbox />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/telefonie"
                          element={
                            <ProtectedRoute>
                              <Belregister />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/ai-meetstaat-test"
                          element={
                            <ProtectedRoute>
                              <AiMeetstaatTest />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/data-inhaal"
                          element={
                            <ProtectedRoute>
                              <DataInhaal />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/ai-organisatie"
                          element={
                            <ProtectedRoute>
                              <AiOrganisatie />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/ai-organisatie/goedkeuringen"
                          element={
                            <ProtectedRoute>
                              <AiOrganisatieGoedkeuringen />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/ai-organisatie/:code"
                          element={
                            <ProtectedRoute>
                              <AiOrganisatieAgent />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/chief"
                          element={
                            <ProtectedRoute>
                              <Chief />
                            </ProtectedRoute>
                          }
                        />
                        {/* Standalone (geen Layout): fullscreen command center voor de kantoor-tv.
                            De pagina doet zelf de auth- en admin-check. */}
                        <Route path="/mission-control" element={<MissionControlPage />} />
                        <Route path="/settings" element={<NaarDashboardTab tab="settings" />} />
                        <Route path="/inbox" element={<NaarDashboardTab tab="inbox" />} />
                        <Route
                          path="/inkoopfacturen"
                          element={<NaarDashboardTab tab="inkoopfacturen" />}
                        />
                        <Route
                          path="/clusters"
                          element={<NaarDashboardTab tab="uitbestedingen" />}
                        />
                        <Route path="/leads/:id" element={<NaarLead />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: 'rgb(var(--color-surface))',
                        color: 'rgb(var(--color-text))',
                        border: '1px solid rgb(var(--color-border))',
                      },
                      success: { duration: 3000 },
                      error: { duration: 5000 },
                    }}
                  />
                </ChatProvider>
              </AuthProvider>
            </ThemeProvider>
          </BrowserRouter>
        </RealtimeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
